import { setValidationLocale } from '@framework/validation/validationLocale.ts';
import dayjs from 'dayjs';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import {
  defaultLocale,
  type LanguageTag,
  languages,
  locales,
} from '../config.ts';
import { i18nNamespaces, i18nResources } from './resources.ts';

// Carico le traduzioni dedicate
if (process.env.TRANSLATIONS_OVERRIDE_RESOURCES) {
  for (const [locale, resources] of Object.entries(
    process.env.TRANSLATIONS_OVERRIDE_RESOURCES,
  )) {
    if (!i18nResources[locale as keyof typeof i18nResources]?.translation) {
      console.warn(`[i18n.override] Locale "${locale}" not trovato`);
      continue;
    }

    // biome-ignore format: <tbd>
    // console.log(`[i18n.override] Aggiunta traduzioni per locale "${locale}"`, resources);

    Object.assign(
      i18nResources[locale as keyof typeof i18nResources].translation,
      resources,
    );
  }
}

interface I18NextInitOptions {
  lookupLocalStoragePrefix: string;
  // Se impostato a true, verrà utilizzata solo la lingua di default.
  // Usato per i microfrontend che non supportano la localizzazione.
  onlyDefaultLocale?: boolean;
}

/**
 * Funzione di inizializzazione di i18next. Prepara l'istanza di i18next
 * in modo da configurare la lingua di default, il fallback e le varie
 * risorse di traduzione.
 */
export function i18nextInit(options: I18NextInitOptions) {
  i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      supportedLngs:
        process.env.ENABLE_LOCALIZATION === 'true' && !options.onlyDefaultLocale
          ? [...languages]
          : [defaultLocale.i18nLocale],
      nonExplicitSupportedLngs: true,
      fallbackLng: defaultLocale.i18nLocale,
      missingKeyNoValueFallbackToKey: false,
      resources: i18nResources,
      ns: i18nNamespaces,
      load: 'all',
      debug: process.env.NODE_ENV === 'development',
      keySeparator: false,
      returnEmptyString: false,
      interpolation: {
        escapeValue: false,
      },
      detection: {
        caches: ['localStorage'],
        order: ['localStorage'],
        lookupLocalStorage: options.lookupLocalStoragePrefix + '_lng',
      },
    });

  i18next.on('languageChanged', (language: string) => {
    setValidationLocale(i18next);
    const { dayjsLocale, tag } =
      language in locales ? locales[language as LanguageTag] : defaultLocale;
    dayjs.locale(dayjsLocale);

    if (typeof window !== 'undefined') {
      document.documentElement.lang = tag;
    }
  });

  // Effettua il trigger del cambio lingua per inizializzare
  // DayJS e Yup con la lingua corretta.
  i18next.changeLanguage(i18next.language);
}

// Esporta l'istanza di i18next configurata
export { i18next };
